<!-- /////////////////////////////////////////////////////////////////////////// TEMPLATE -->

<template>
    <main-layout>
        <!-- Loaded -->
        <div v-if="!pageLoading" class="sl-page">
            <!-- Page Header -->
            <div class="sl-page__header flex justify-between">
                <!-- Page Title -->
                <div class="sl-page__title">
                    {{ $t('orders.title') }}
                </div>
            </div>

            <div class="sl-page__tools">
                <!-- Search Field -->
                <div class="sl-search">
                    <mdi-icon icon="mdiMagnify" />

                    <input
                        ref="searchField"
                        v-model="searchText"
                        type="text"
                        class="sl-search__field"
                        @input="searchField_OnInput"
                    >

                    <tippy class="absolute top-1.5 right-2.5">
                        <template #content>
                            {{ $t('orders.advanced_filters.toggle') }}
                        </template>

                        <mdi-icon
                            :icon="showAdvancedFilters ? 'mdiFilter' : 'mdiFilterOutline'"
                            class="cursor-pointer w-[20px] h-[20px] text-gray-400 transition-colors outline-none hover:text-primary-500 focus-visible:text-primary-500"
                            :class="{ 'text-primary-300': showAdvancedFilters }"
                            tabindex="0"
                            @click="toggleAdvancedFilters"
                            @keypress.enter="toggleAdvancedFilters"
                        />
                    </tippy>
                </div>

                <!-- CSV Export Button -->
                <lsn-loader-drop-down-button
                    v-if="$store.getters['account/can']('price:view')"
                    class="ml-2"
                    :class="{
                        'lsn-btn--primary-outline': !exportingCsv,
                        'lsn-btn--disabled': exportingCsv,
                    }"
                    :is-loading="exportingCsv"
                    :label="$t('orders.export_csv')"
                    :options="[
                        {
                            label: $t('orders.export_csv'),
                            onclick: () => exportCsv('csv'),
                            default: true,
                        },
                        {
                            label: $t('orders.export_csv') + ' (Excel)',
                            onclick: () => exportCsv('excel'),
                        },
                    ]"
                    @click="exportCsv('csv')"
                />

                <div class="grow"></div>

                <!-- Pagination -->
                <div v-if="!searching && orders.length" class="pagination">
                    <!-- Results per Page -->
                    <tippy>
                        <template #content>
                            {{ $t('pagination.results_per_page.label') }}
                        </template>

                        <div class="pagination__page-size relative">
                            <span>{{ pageSize }}</span>

                            <mdi-icon icon="mdiMenuDown" />

                            <select v-model="pageSize">
                                <option
                                    v-for="value in pageSizes"
                                    :key="`ps${value}`"
                                    :value="value"
                                >
                                    {{ value }}
                                </option>
                            </select>
                        </div>
                    </tippy>

                    <!-- Previous Button -->
                    <div v-if="cPage !== 1" class="pagination__page" @click="previous">
                        &lt;
                    </div>

                    <!-- Pages -->
                    <template v-for="index in pages">
                        <div
                            v-if="typeof index === 'number'" :key="`a${index}`"
                            class="pagination__page"
                            :class="{ 'pagination__page--active' : index === cPage }"
                            @click="setCurrentPage(index)"
                        >
                            {{ index }}
                        </div>

                        <div v-else :key="`b${index}`" class="pagination__spacer">
                            {{ index }}
                        </div>
                    </template>

                    <!-- Next Button -->
                    <div v-if="cPage !== totalPages" class="pagination__page" @click="next">
                        &gt;
                    </div>
                </div>

                <!-- Re-Order Button -->
                <div class="lsn-btn ml-4" :class="reOrderButtonClass" @click="reOrder('top')">
                    {{ $t('orders.reorder_btn.label') }}

                    <template v-if="reOrders.length">
                        ({{ reOrders.length }})
                    </template>
                </div>

                <div class="w-full"></div>

                <!-- Advanced Filters -->
                <div v-if="showAdvancedFilters" class="advanced-filters">
                    <div class="advanced-filters-arrow"></div>

                    <div class="mb-2">
                        <div class="inline-block align-middle text-base text-primary-600">
                            {{ $t('orders.advanced_filters.title') }}
                        </div>

                        <!-- Clear Filters Button -->
                        <div
                            v-if="hasAdvancedFilters"
                            class="cursor-pointer inline-block align-middle ml-2 text-primary-500 text-xs font-normal hover:underline focus-visible:underline"
                            @click="clearAdvancedFilters"
                        >
                            {{ $t('common.clear_filters') }}
                        </div>
                    </div>

                    <div class="flex gap-[var(--gap)] w-full">
                        <!-- Left Side: Date Filters -->
                        <div class="advanced-filters__left">
                            <!-- Filter: From Date -->
                            <div
                                v-click-outside="closeDatePickerFrom"
                                class="advanced-filter advanced-filter--button"
                                :class="{ 'advanced-filter--has-value': advancedFilters.dateFrom }"
                                @click.self="showDatePickerFrom = !showDatePickerFrom"
                            >
                                <mdi-icon icon="mdiCalendarArrowRight" class="advanced-filter__icon mr-2" />

                                <span class="advanced-filter__caption">
                                    <template v-if="advancedFilters.dateFrom">
                                        {{ formatDate(advancedFilters.dateFrom) }}
                                    </template>

                                    <template v-else>
                                        {{ $t('orders.advanced_filters.date_from.label') }}
                                    </template>
                                </span>

                                <div
                                    v-if="showDatePickerFrom"
                                    class="absolute left-1 top-full mt-1 z-10"
                                >
                                    <v-date-picker
                                        v-model="advancedFilters.dateFrom"
                                        :locale="$route.params.lang"
                                        class="shadow-md"
                                        color="primary"
                                        @dayclick="closeDatePickerFrom"
                                    />
                                </div>
                            </div>

                            <!-- Filter: To Date -->
                            <div
                                v-click-outside="closeDatePickerTo"
                                class="advanced-filter advanced-filter--button"
                                :class="{ 'advanced-filter--has-value': advancedFilters.dateTo }"
                                @click.self="showDatePickerTo = !showDatePickerTo"
                            >
                                <mdi-icon icon="mdiCalendarArrowLeft" class="advanced-filter__icon mr-2" />

                                <span class="advanced-filter__caption">
                                    <template v-if="advancedFilters.dateTo">
                                        {{ formatDate(advancedFilters.dateTo) }}
                                    </template>

                                    <template v-else>
                                        {{ $t('orders.advanced_filters.date_to.label') }}
                                    </template>
                                </span>

                                <div
                                    v-if="showDatePickerTo"
                                    class="absolute left-1 top-full mt-1 z-10"
                                >
                                    <v-date-picker
                                        v-model="advancedFilters.dateTo"
                                        :locale="$route.params.lang"
                                        class="shadow-md"
                                        color="primary"
                                        @dayclick="closeDatePickerTo"
                                    />
                                </div>
                            </div>
                        </div>

                        <!-- Right Side: Status & Article Filters -->
                        <div class="advanced-filters__right">
                            <!-- Level-1 Filters -->
                            <div class="flex items-center gap-1 w-full">
                                <filter-select
                                    v-model="advancedFilters.articleType"
                                    :label="$t('orders.advanced_filters.article_type.label')"
                                    :options="{
                                        '': [
                                            {
                                                label: $t('common.lenses'),
                                                value: 'LENS',
                                            },
                                            {
                                                label: $t('cleaning_products.page_title'),
                                                value: 'CLEANING_PRODUCT',
                                            },
                                            {
                                                label: $t('accessories.page_title'),
                                                value: 'ACCESSORY',
                                            },
                                        ],
                                    }"
                                    radio
                                    class="grow max-w-[25%]"
                                />

                                <!-- Filter: Status -->
                                <filter-select
                                    v-model="advancedFilters.statusCodes"
                                    :label="$t('orders.advanced_filters.status.label')"
                                    :options="{
                                        '': [
                                            {
                                                label: $t('orders.advanced_filters.status.any'),
                                                value: null,
                                            },
                                            ...statuses,
                                        ],
                                    }"
                                    radio
                                    class="grow max-w-[25%]"
                                />

                                <!-- Filter: Patient Reference -->
                                <filter-select
                                    v-model="advancedFilters.patientReference1"
                                    :label="$t('orders.advanced_filters.patient_reference.label')"
                                    :options="{
                                        '': patients.map(p => ({
                                            label: p,
                                            value: p,
                                        })),
                                    }"
                                    class="grow max-w-[25%]"
                                />

                                <!-- Filter: External Reference -->
                                <filter-select
                                    v-model="advancedFilters.extRef"
                                    :label="$t('orders.advanced_filters.external_reference.label')"
                                    :options="{
                                        '': extRefs.map(r => ({
                                            label: r,
                                            value: r,
                                        })),
                                    }"
                                    class="grow max-w-[25%]"
                                />
                            </div>

                            <!-- Level-2 Filters -->
                            <div class="flex flex-wrap items-center gap-1">
                                <template v-if="advancedFilters.articleType === 'LENS'">
                                    <!-- Filter: Lens Type -->
                                    <filter-select
                                        v-model="advancedFilters.lensType"
                                        :label="$t('orders.advanced_filters.lens_type.label')"
                                        :options="{ '': lensTypes }"
                                        class="grow max-w-[25%]"
                                    />

                                    <!-- Filter: Lens Name -->
                                    <filter-select
                                        v-model="advancedFilters.lensName"
                                        :label="$t('orders.advanced_filters.lens_name.label')"
                                        :options="{ '': lensNames }"
                                        class="grow max-w-[25%]"
                                    />

                                    <!-- Filter: Lens Family -->
                                    <filter-select
                                        v-model="advancedFilters.lensFamily"
                                        :label="$t('orders.advanced_filters.lens_family.label')"
                                        :options="{ '': lensFamilies }"
                                        class="grow max-w-[25%]"
                                    />

                                    <!-- Filter: Lens Design -->
                                    <filter-select
                                        v-model="advancedFilters.lensDesign"
                                        :label="$t('orders.advanced_filters.lens_design.label')"
                                        :options="{ '': lensDesigns }"
                                        class="grow max-w-[25%]"
                                    />

                                    <!-- Filter: Front Geometry -->
                                    <filter-select
                                        v-model="advancedFilters.frontGeometry"
                                        :label="$t('orders.advanced_filters.front_geometry.label')"
                                        :options="{ '': frontGeometries }"
                                        radio
                                        class="grow max-w-[25%]"
                                    />

                                    <!-- Filter: Multifocal -->
                                    <filter-select
                                        v-model="advancedFilters.multifocal"
                                        :label="$t('orders.advanced_filters.multifocal.label')"
                                        :options="{ '': multifocalOptions }"
                                        radio
                                        class="grow max-w-[25%]"
                                    />

                                    <!-- Filter: Peripheral Geometry -->
                                    <filter-select
                                        v-model="advancedFilters.peripheralGeometry"
                                        :label="$t('orders.advanced_filters.peripheral_geometry.label')"
                                        :options="{ '': peripheralGeometries }"
                                        radio
                                        class="grow max-w-[25%]"
                                    />

                                    <!-- Filter: Return Option -->
                                    <filter-select
                                        v-model="advancedFilters.returnOption"
                                        :label="$t('orders.advanced_filters.return_option.label')"
                                        :options="{ '': returnOptions }"
                                        radio
                                        class="grow max-w-[25%]"
                                    />

                                    <!-- Filter: Renewal Period -->
                                    <filter-select
                                        v-model="advancedFilters.renewalPeriod"
                                        :label="$t('orders.advanced_filters.renewal_period.label')"
                                        :options="{ '': renewalPeriods }"
                                        radio
                                        class="grow max-w-[25%]"
                                    />
                                </template>

                                <template v-if="advancedFilters.articleType === 'CLEANING_PRODUCT'">
                                    <!-- Filter: Intended Use -->
                                    <filter-select
                                        v-model="advancedFilters.intendedUse"
                                        :label="$t('orders.advanced_filters.intended_use.label')"
                                        :options="{ '': intendedUses }"
                                        class="grow max-w-[25%]"
                                    />

                                    <!-- Filter: Product Family -->
                                    <filter-select
                                        v-model="advancedFilters.productFamily"
                                        :label="$t('orders.advanced_filters.product_family.label')"
                                        :options="{ '': productFamilies }"
                                        class="grow max-w-[25%]"
                                    />
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Page Content -->
            <div v-if="!resultsLoading" class="sl-page__content">
                <template v-if="orders.length">
                    <!-- Order Table -->
                    <sl-table
                        :columns="columns"
                        :rows="orders"
                        @remove="remove"
                        @add-to-reorder="addToReOrder"
                        @remove-from-reorder="removeFromReOrder"
                    />

                    <!-- Re-Order Button -->
                    <div class="flex justify-end ml-auto my-4">
                        <div class="lsn-btn" :class="reOrderButtonClass" @click="reOrder('bottom')">
                            {{ $t('orders.reorder_btn.label') }}

                            <template v-if="reOrders.length">
                                ({{ reOrders.length }})
                            </template>
                        </div>
                    </div>
                </template>

                <template v-else>
                    <div class="text-gray-500 text-sm mt-4 text-left">
                        {{ $t('common.search.no_result') }}
                    </div>
                </template>
            </div>

            <div v-else class="mt-8 text-center">
                <img class="inline" src="/img/loader-5.gif">
            </div>
        </div>

        <!-- Page Loading -->
        <template v-else>
            <div class="mt-8 mx-auto">
                <img class="inline" src="/img/loader-5.gif">
            </div>
        </template>

        <!-- NightFlex v8 Reorder Dialog -->
        <night-flex-v8-dialog
            :is-open="showNightFlexV8Dialog"
            :selected="selectedNightFlexV8"
            @close="showNightFlexV8Dialog = false"
        />
    </main-layout>

</template>

<!-- /////////////////////////////////////////////////////////////////////////// SCRIPT -->

<script>

import { markRaw, shallowRef } from 'vue';
import { DatePicker as VDatePicker } from 'v-calendar';
import { debounce, clone } from 'lodash-es';
import { useToast } from 'vue-toastification';
import LensOrderUrlManager from './lenses/LensOrderUrlManager.js';
import moment from 'moment';
import MainLayout from '@/layouts/MainLayout.vue';
import SlTable from '@/components/sl-table/SlTable.vue';
import LensOrderTableDetails from '@/components/order/LensOrderTableDetails.vue';
import DefaultOrderTableDetails from '@/components/order/DefaultOrderTableDetails.vue';
import NonLensPackOrderTableDetails from '@/components/order/NonLensPackOrderTableDetails.vue';
import OrderIdentificationCell from '@/components/sl-table/sl-cells/OrderIdentificationCell.vue';
import OrderDetailsCell from '@/components/sl-table/sl-cells/OrderDetailsCell.vue';
import OrderStatusCell from '@/components/sl-table/sl-cells/OrderStatusCell.vue';
import OrderPriceCell from '@/components/sl-table/sl-cells/OrderPriceCell.vue';
import ReOrderCell from '@/components/sl-table/sl-cells/ReOrderCell.vue';
import NightFlexV8Dialog from './NightFlexV8Dialog.vue';
import constants from '@/constants/constants';
import axios from '@/axios';
import LsnLoaderDropDownButton from '@/components/LsnLoaderDropDownButton.vue';
import FilterSelect from '@/components/FilterSelect.vue';

function newAdvancedFilters()
{
    return {
        dateFrom:          null,
        dateTo:            null,
        articleType:       null,
        statusCodes:       null,
        patientReference1: [],
        extRef:            [],

        // Level-2 filters
        lensType:           [],
        lensName:           [],
        lensFamily:         [],
        lensDesign:         [],
        frontGeometry:      null,
        multifocal:         null,
        peripheralGeometry: null,
        returnOption:       null,
        renewalPeriod:      null,

        productFamily: [],
        intendedUse:   [],
    };
}

export default
{
    name: 'OrdersList',

    components:
    {
        MainLayout,
        SlTable,
        VDatePicker,
        NightFlexV8Dialog,
        FilterSelect,
        LsnLoaderDropDownButton,
    },

    data()
    {
        return {
            pageLoading:    true,
            resultsLoading: false,
            searching:      false,
            searchText:     '',
            pageSize:       10,
            cPage:          1,
            reOrders:       [], // Array of orders to re-order (max 2)

            showNightFlexV8Dialog: false,

            // Advanced filters
            showAdvancedFilters: false,
            advancedFilters:     newAdvancedFilters(),

            showDatePickerFrom: false,
            showDatePickerTo:   false,

            extRefs: [],

            fetchingPatients: false,
            fetchingExtRefs:  false,
            exportingCsv:     false,
        };
    },

    computed:
    {
        // SL Table definition
        columns()
        {
            return [
                {
                    key:        'odate',
                    tlabel:     'order_table.headers.date',
                    formatters: 'datef:DD.MM.YYYY',
                    align:      'center',
                    cssClass:   'text-gray-400 text-xs',
                },
                {
                    key:           'reference',
                    tlabel:        'order_table.headers.reference',
                    cellComponent: markRaw(OrderIdentificationCell),
                },
                {
                    key:           'article',
                    tlabel:        'order_table.headers.article',
                    cellComponent: markRaw(OrderDetailsCell),
                },
                {
                    key:           'status',
                    tlabel:        'order_table.headers.status',
                    align:         'center',
                    cellComponent: markRaw(OrderStatusCell),
                },
                {
                    tlabel:        'order_table.headers.price',
                    align:         'right',
                    unit:          this.currency,
                    cellComponent: markRaw(OrderPriceCell),
                },
                {
                    align:         'right',
                    cellComponent: markRaw(ReOrderCell),
                },
            ];
        },

        currency()
        {
            const orders = this.$store.state.orders.set;
            if(orders.length === 0)
            {
                return '';
            }
            return orders[0].currency_code;
        },

        //---------------------------------------------------------------------- rows

        orders()
        {
            const orders = this.$store.state.orders.set.map(o => ({ ...o }));
            const reOrderIds = this.reOrders.map(reOrder => reOrder.id);

            for(const order of orders)
            {
                order.is_reorder_added = reOrderIds.includes(order.id);

                order.details_component = null;
                switch(order.order_class)
                {
                    case 'App\\Orders\\Orders\\LensOrder':
                        order.details_component = markRaw(LensOrderTableDetails);
                        break;

                    case 'App\\Orders\\Orders\\PackOrder':
                        if(order.child_orders.every(o => o.order_class !== 'App\\Orders\\Orders\\LensOrder'))
                        {
                            order.details_component = NonLensPackOrderTableDetails;
                        }
                        break;

                    default:
                        if(this.$store.getters['account/isStaff'])
                        {
                            order.details_component = markRaw(DefaultOrderTableDetails);
                        }
                        break;
                }

                // Mark child orders for removal, because we'll render them in
                // the details component of their parent (pack) order instead.
                if(
                    order.pack_order_id &&
                    order.order_class !== 'App\\Orders\\Orders\\LensOrder' // keep lens child orders
                )
                {
                    order.REMOVE_ME = true;
                    continue;
                }

                // Trial Sets: remove the pack line, but keep its contents
                // as usual, i.e. still display each lens as its own order line.
                if(
                    order.order_class === 'App\\Orders\\Orders\\PackOrder' &&
                    order.article.tags.some(t => t.code === 'TRIAL_SET') &&
                    order.child_orders.every(o => o.order_class === 'App\\Orders\\Orders\\LensOrder')
                )
                {
                    order.REMOVE_ME = true;
                    continue;
                }
            }

            return orders.filter(o => !o.REMOVE_ME);
        },

        //---------------------------------------------------------------------- re-order

        reOrderButtonClass()
        {
            if(this.reOrders.length)
            {
                return 'lsn-btn--primary';
            }

            return [
                'lsn-btn--gray-outline',
                'cursors-not-allowed',
            ];
        },

        //---------------------------------------------------------------------- pagination and filters

        pageSizes()
        {
            return [10, 25, 50];
        },

        total()
        {
            return this.$store.state.orders.total;
        },

        totalPages()
        {
            return Math.ceil(this.total / this.pageSize) || 0;
        },

        pages()
        {
            if(!this.total)
            {
                return [];
            }

            const pages = [];

            if(this.totalPages <= 5)
            {
                for(let i = 1; i <= this.totalPages; i += 1)
                {
                    pages.push(i);
                }
            }
            else
            {
                let start = this.cPage - 1;
                let end = this.cPage + 1;

                if(start < 2)
                {
                    const delta = Math.abs(2 - start);
                    start += delta;
                    end   += delta;
                }

                if(end > (this.totalPages - 1))
                {
                    const delta = Math.abs(end - (this.totalPages - 1));
                    start -= delta;
                    end   -= delta;
                }

                pages.push(1);

                if(start > 2)
                {
                    pages.push('…');
                }

                for(let i = start; i <= end; i += 1)
                {
                    pages.push(i);
                }

                if(end < (this.totalPages - 1))
                {
                    pages.push('…');
                }

                if(end < this.totalPages)
                {
                    pages.push(this.totalPages);
                }
            }

            return pages;
        },

        statuses()
        {
            const s = constants.orders.status; // shortcut
            return [
                {
                    code:  s.WAITING_FOR_REVIEW,
                    label: this.$t(`order_table.status.${s.WAITING_FOR_REVIEW}.label`),
                },
                {
                    code:  s.ON_HOLD,
                    label: this.$t(`order_table.status.${s.ON_HOLD}.label`),
                },
                {
                    // Group together statuses that are have the same label
                    code:  `${s.WAITING_FOR_DISPATCH}|${s.DISPATCHED}|${s.OUTSOURCED}|${s.WAITING_FOR_PRODUCTION}`,
                    label: this.$t(`order_table.status.${s.WAITING_FOR_DISPATCH}.label`),
                },
                {
                    code:  s.PRODUCTION,
                    label: this.$t(`order_table.status.${s.PRODUCTION}.label`),
                },
                {
                    code:  s.WAITING_FOR_SHIPPING,
                    label: this.$t(`order_table.status.${s.WAITING_FOR_SHIPPING}.label`),
                },
                {
                    code:  s.SHIPPED,
                    label: this.$t(`order_table.status.${s.SHIPPED}.label`),
                },
            ].map(s => ({
                // Uppercase first letter of label
                label: s.label.slice(0, 1).toUpperCase() + s.label.slice(1),
                value: s.code,
            }));
        },

        patients()
        {
            return this.$store.state.patients.all;
        },

        lensTypes()
        {
            return [
                'SOFT',
                'RGP',
                'SCLERAL',
                'ORTHOK',
            ].filter(
                t => this.$store.getters['catalog/hasActiveArticlesByTag'](t)
            ).map(t => ({
                label: this.$store.getters['catalog/getTagLabelByCode'](t),
                value: t,
            }));
        },

        lensNames()
        {
            return [
                'ORBIS',
                'TORIS',
                'BORELIS',
                'TORELIS',
                'RELAX',
                'LUNELLE',
                'HYDROCONE',
                'HYDROMED',
                'HYDROCOLOR',
                'HYDROCOLOR_PRO',
                'ORBIFLEX',
                'TORIFLEX',
                'BORIFLEX',
                'TORELIFLEX',
                'RELAXFLEX',
                'FLEXCONE',
                'FLEXMED',
                'NIGHTFLEX',
                'SCLERAFLEX',
                'PERMO_PLUS',
                'PERSECON',
            ].filter(
                t => this.$store.getters['catalog/hasActiveArticlesByTag'](t)
            ).map(t => ({
                label: this.$store.getters['catalog/getTagLabelByCode'](t),
                value: t,
            }));
        },

        lensFamilies()
        {
            return [
                'COLORED',
                'MYOPIA_CONTROL',
                'KERATOCONUS',
                'CRISTALENS',
                'ARCAFLEX',
                'MULTIFLEX',
            ].filter(
                t => this.$store.getters['catalog/hasActiveArticlesByTag'](t)
            ).map(t => ({
                label: this.$store.getters['catalog/getTagLabelByCode'](t),
                value: t,
            }));
        },

        lensDesigns()
        {
            return [
                'EVO_LENS',
                'SXS_LENS',
                'ASP_LENS',
                'EX_LENS',
                'SA_LENS',
                'S2A_LENS',
                'S3S_LENS',
                'SMS_LENS',
                'K12_LENS',
                'K34_LENS',
            ].filter(
                t => this.$store.getters['catalog/hasActiveArticlesByTag'](t)
            ).map(t => ({
                label: this.$store.getters['catalog/getTagLabelByCode'](t),
                value: t,
            }));
        },

        frontGeometries()
        {
            return [
                'SPHERICAL',
                'TORIC',
            ].filter(
                t => this.$store.getters['catalog/hasActiveArticlesByTag'](t)
            ).map(t => ({
                label: this.$store.getters['catalog/getTagLabelByCode'](t),
                value: t,
            }));
        },

        multifocalOptions()
        {
            return [
                {
                    label: this.$t('common.yes'),
                    value: 'MULTIFOCAL',
                },
                {
                    label: this.$t('common.no'),
                    value: 'UNIFOCAL',
                },
            ];
        },

        peripheralGeometries()
        {
            return [
                'SYMMETRICAL',
                'ASYMMETRICAL',
                'QUADRANT',
            ].filter(
                t => this.$store.getters['catalog/hasActiveArticlesByTag'](t)
            ).map(t => ({
                label: this.$store.getters['catalog/getTagLabelByCode'](t),
                value: t,
            }));
        },

        returnOptions()
        {
            return [
                'SIMPLE',
                'CLASSIC',
                'CLASSIC_PLUS',
                'FITTING_LENS',
            ].filter(
                t => this.$store.getters['catalog/hasActiveArticlesByTag'](t)
            ).map(t => ({
                label: this.$store.getters['catalog/getTagLabelByCode'](t),
                value: t,
            }));
        },

        renewalPeriods()
        {
            return [
                'PERIOD_3_MONTHS',
                'PERIOD_6_MONTHS',
                'PERIOD_12_MONTHS',
            ].filter(
                t => this.$store.getters['catalog/hasActiveArticlesByTag'](t)
            ).map(t => ({
                label: this.$store.getters['catalog/getTagLabelByCode'](t),
                value: t,
            }));
        },

        intendedUses()
        {
            return [
                'COMPATIBLE_WITH_SOFT',
                'COMPATIBLE_WITH_RIGID',
                'MULTI_PURPOSE',
                'OXIDIZER',
                'PRESERVATION',
                'CLEANER',
                'MULTI_PACK',
                'FLIGHT_PACK',
                'ARTIFICIAL_TEARS',
                'COMPATIBLE_WITH_HYDROCOLOR_PRO',
            ].filter(
                t => this.$store.getters['catalog/hasActiveArticlesByTag'](t)
            ).map(t => ({
                label: this.$store.getters['catalog/getTagLabelByCode'](t),
                value: t,
            }));
        },

        productFamilies()
        {
            return [
                'ALL_CLEAN',
                'ALL_IN_ONE',
                'CLEANFLEX',
                'ONE_STEP_T',
                'STOREFLEX',
                'ENZYME',
                'FRESH',
                'UNIFRESH',
            ].filter(
                t => this.$store.getters['catalog/hasActiveArticlesByTag'](t)
            ).map(t => ({
                label: this.$store.getters['catalog/getTagLabelByCode'](t),
                value: t,
            }));
        },

        hasAdvancedFilters()
        {
            return Object.values(this.advancedFilters).some(f =>
            {
                if(Array.isArray(f))
                {
                    return f.length > 0;
                }
                return !!f;
            });
        },

        /**
         * The `reOrders` array, sorted with unknown-side lenses at the end.
         */
        sortedReOrders()
        {
            const sides = ['right', 'left'];
            return this.reOrders.slice().sort((a, b) =>
            {
                const sideA = a.article_data.LENS_SIDE;
                const sideB = b.article_data.LENS_SIDE;

                const aHasSide = sides.includes(sideA);
                const bHasSide = sides.includes(sideB);

                if(
                    aHasSide && bHasSide ||
                    !aHasSide && !bHasSide
                )
                {
                    return 0;
                }
                else if(aHasSide)
                {
                    return -1;
                }

                return 1;
            });
        },

        /**
         * @returns {{right?: Object, left?: Object}}
         */
        selectedNightFlexV8()
        {
            const selected = {};
            for(const side of ['right', 'left'])
            {
                const order = this.reOrders.find(
                    o => o.article_data.LENS_SIDE === side &&
                        this.isNightFlexV8(o)
                );

                if(order)
                {
                    selected[side] = order;
                }
            }

            return selected;
        },
    },

    watch:
    {
        pageSize()
        {
            this.searchOrders();
        },

        advancedFilters:
        {
            handler()
            {
                this.checkDatesOrder();
                this.searchOrders();
            },
            deep: true,
        },
    },

    mounted()
    {
        this.searchOrders()
            .then(() =>
            {
                this.pageLoading = false;

                this.$nextTick(() => this.$refs.searchField?.focus());
            });
    },

    methods:
    {
        //---------------------------------------------------------------------- rows

        searchOrders()
        {
            this.resultsLoading = true;

            // If the current page number is greater than the number of pages
            // for the new page size, reset the cursor to page #1.
            if((this.cPage - 1) * this.pageSize > this.total)
            {
                this.cPage = 1;
            }

            const af = this.advancedFilters;
            const filters = {};
            if(af.dateFrom)                  filters.dateFrom           = this.formatDate(af.dateFrom, 'api');
            if(af.dateTo)                    filters.dateTo             = this.formatDate(af.dateTo, 'api');
            if(af.articleType)               filters.articleType        = af.articleType;
            if(af.statusCodes)               filters.status             = af.statusCodes;
            if(af.patientReference1?.length) filters.patientRef         = af.patientReference1;
            if(af.extRef?.length)            filters.extRef             = af.extRef;
            if(af.lensType?.length)          filters.lensType           = af.lensType;
            if(af.lensName?.length)          filters.lensName           = af.lensName;
            if(af.lensFamily?.length)        filters.lensFamily         = af.lensFamily;
            if(af.lensDesign?.length)        filters.lensDesign         = af.lensDesign;
            if(af.frontGeometry)             filters.frontGeometry      = af.frontGeometry;
            if(af.multifocal)                filters.multifocal         = af.multifocal;
            if(af.peripheralGeometry)        filters.peripheralGeometry = af.peripheralGeometry;
            if(af.returnOption)              filters.returnOption       = af.returnOption;
            if(af.renewalPeriod)             filters.renewalPeriod      = af.renewalPeriod;
            if(af.productFamily?.length)     filters.productFamily      = af.productFamily;
            if(af.intendedUse?.length)       filters.intendedUse        = af.intendedUse;

            const options = {
                offset: (this.cPage - 1) * this.pageSize,
                limit:  this.pageSize,
                query:  this.searchText,
                filters,
            };

            return this.$store.dispatch('orders/searchOrders', options)
                .then(orders =>
                {
                    this.searching = false;
                    this.resultsLoading = false;

                    return orders;
                });
        },

        //---------------------------------------------------------------------- pagination and filters

        previous()
        {
            this.setCurrentPage(this.cPage - 1);
        },

        next()
        {
            this.setCurrentPage(this.cPage + 1);
        },

        setCurrentPage(pageNumber, forceSearch = false)
        {
            pageNumber = parseInt(pageNumber);
            if(this.cPage !== pageNumber)
            {
                this.cPage = pageNumber;

                // Load search results for the new page
                this.searchOrders();
            }
            else if(forceSearch)
            {
                this.searchOrders();
            }
        },

        searchField_OnInput: debounce(function()
        {
            // Always reload results from page #1
            this.searching = true;
            this.setCurrentPage(1, true);
        }, 800),

        toggleAdvancedFilters()
        {
            if(this.showAdvancedFilters)
            {
                // Collapse filters
                this.showAdvancedFilters = false;
            }
            else
            {
                // Expand filters
                this.showAdvancedFilters = true;

                // Fetch patients & ext refs (if not already done)
                if(!this.patients.length)
                {
                    this.fetchPatients();
                }

                if(!this.extRefs.length)
                {
                    this.fetchExtRefs();
                }
            }
        },

        clearAdvancedFilters()
        {
            this.advancedFilters = newAdvancedFilters();
        },

        fetchPatients()
        {
            if(this.fetchingPatients)
            {
                return;
            }

            this.fetchingPatients = true;
            const eid = this.$store.state.account.cEntity.id;
            this.$store.dispatch('patients/fetchAll', eid)
                .then(patients =>
                {
                    // nothing
                })
                .catch(error =>
                {
                    // todo: handle error
                    console.log(error);
                })
                .then(() =>
                {
                    this.fetchingPatients = false;
                });
        },

        fetchExtRefs()
        {
            if(this.fetchingExtRefs)
            {
                return;
            }

            this.fetchingExtRefs = true;
            const eid = this.$store.state.account.cEntity.id;
            new Promise((resolve, reject) =>
            {
                axios.get(`/api/ext-refs/entity/${eid}`)
                    .then(({ data: extRefs }) => resolve(extRefs))
                    .catch(error => reject(error));
            })
                .then(extRefs =>
                {
                    this.extRefs = extRefs;
                })
                .catch(error =>
                {
                    this.extRefs = [];

                    // todo: handle error
                })
                .then(() =>
                {
                    this.fetchingExtRefs = false;
                });
        },

        formatDate(date, context = 'display')
        {
            if(!date)
            {
                return null;
            }

            switch(context)
            {
                case 'api':
                    return parseInt(moment(date).format('YYYYMMDD'));

                case 'display':
                default:
                    return moment(date).format('DD.MM.YYYY');
            }
        },

        openDatePickerFrom()
        {
            this.showDatePickerFrom = true;
        },

        closeDatePickerFrom()
        {
            this.showDatePickerFrom = false;
        },

        openDatePickerTo()
        {
            this.showDatePickerTo = true;
        },

        closeDatePickerTo()
        {
            this.showDatePickerTo = false;
        },

        /**
         * Ensure `dateFrom` is never greater than `dateTo`
         * by swapping them if necessary.
         *
         * @returns {void}
         */
        checkDatesOrder()
        {
            if(!this.advancedFilters.dateFrom || !this.advancedFilters.dateTo)
            {
                return;
            }

            if(this.advancedFilters.dateFrom > this.advancedFilters.dateTo)
            {
                [this.advancedFilters.dateFrom, this.advancedFilters.dateTo] = [this.advancedFilters.dateTo, this.advancedFilters.dateFrom];
            }
        },

        //---------------------------------------------------------------------- CSV export

        async exportCsv(format = 'csv')
        {
            if(this.exportingCsv)
            {
                return;
            }

            this.exportingCsv = true;

            try
            {
                const eid = this.$store.state.account.cEntity.id;
                const fileUrl = `/api/order/entity/${eid}/csv?format=${format}`;

                const response = await axios.get(fileUrl, { responseType: 'blob' });

                const blob = response.data;
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `orders-${moment().format('YYYYMMDD-HHmmss')}.csv`;
                a.click();
                URL.revokeObjectURL(url);
            }
            catch(e)
            {
                // Nothing
            }
            finally
            {
                this.exportingCsv = false;
            }
        },

        //---------------------------------------------------------------------- order management

        addToReOrder(order)
        {
            const side = order.article_data.LENS_SIDE;

            if(['left', 'right'].indexOf(side) !== -1)
            {
                for(const i in this.reOrders)
                {
                    const reOrder = this.reOrders[i];

                    if(reOrder.article_data.LENS_SIDE === side)
                    {
                        this.reOrders[i] = clone(order);

                        return;
                    }
                }
            }
            else
            {
                if(this.reOrders.length >= 2)
                {
                    this.reOrders.shift();
                }
            }


            this.reOrders.push(clone(order));
        },

        removeFromReOrder(order)
        {
            for(const i in this.reOrders)
            {
                const reOrder = this.reOrders[i];

                if(reOrder.id == order.id)
                {
                    this.reOrders.splice(i, 1);

                    return;
                }
            }
        },

        reOrder(position)
        {
            if(!this.reOrders.length)
            {
                return;
            }

            this.$gtag.event('btn-reorder', { position });

            // Make sure orders with unknown side are last
            const sides = ['right', 'left'];

            // Build the target URL
            const um = new LensOrderUrlManager(this.$route);
            for(const order of this.sortedReOrders)
            {
                const side = order.article_data.LENS_SIDE;
                if(sides.includes(side))
                {
                    um.setOrderReference(side, order.reference);
                }
                else if(um.has('right'))
                {
                    um.setOrderReference('left', order.reference);
                }
                else
                {
                    um.setOrderReference('right', order.reference);
                }

                // Detect NightFlex v8 reorders and ask for the CLSIMID.
                if(this.isNightFlexV8(order) && this.$store.getters['account/isDeveloper'])
                {
                    // todo: use "account/isStaff" instead of "account/isDeveloper"
                    //       once NFv8 renewal is validated.
                    this.showNightFlexV8Dialog = true;
                    return;
                }
            }

            this.$router.push({ path: um.getPath() });
        },

        remove(order)
        {
            axios.delete(`/api/order/reference/${order.reference}`)
                .then(response =>
                {
                    useToast().success(this.$t('order.delete_success'));

                    this.searchOrders().then(() =>
                    {
                        this.$store.dispatch('orders/setDeletionRequested', { orderId: order.id });
                    });
                })
                .catch(error =>
                {
                    if(error.response.status !== 403)
                    {
                        throw error;
                    }

                    // The order cannot be deleted because it is already being shipped.
                    // => Display a message to the user.
                    useToast().error(this.$t('order_table.details.request_deletion.forbidden'));
                });
        },

        isNightFlexV8(order)
        {
            return /^NIGHTFLEX_\w+$/.test(order.article_data.PROTOTYPE_CODE);
        },
    },
};
</script>


<!-- /////////////////////////////////////////////////////////////////////////// STYLE -->

<style scoped>
@reference "#main.css";


.sl-page__tools
{
    @apply flex flex-wrap justify-between items-center mb-4;
}

.sl-search
{
    @apply relative;
}

.sl-search > .mdi-icon
{
    @apply absolute top-1.5 left-2.5 w-[22px] h-[22px] text-gray-400 pointer-events-none;
}

.sl-search__field
{
    @apply border border-gray-300 px-[34px] w-full h-8 text-primary-500 rounded-full appearance-none;
}

.sl-search__field:focus
{
    @apply outline-none;
}

.pagination
{
    @apply flex ml-auto select-none;
}

.pagination__page-size
{
    @apply cursor-pointer relative flex justify-between items-center border border-gray-300 pl-2 min-w-8 h-8 shrink-0 text-gray-500 text-sm;

    & > span:first-child
    {
    }

    & > .mdi-icon
    {
        @apply ml-1 w-5 h-5 shrink-0 text-gray-500;
    }

    & > select
    {
        @apply cursor-pointer absolute top-0 right-0 bottom-0 left-0 opacity-0;
    }
}

.pagination__page
{
    @apply cursor-pointer flex justify-center items-center ml-2 p-2 border border-primary-100 min-w-8 h-8 shrink-0 text-primary-500 text-sm
        hover:bg-primary-50;
}

.pagination__page--active
{
    @apply bg-primary-100;
}

.pagination__spacer
{
    height: 32px;
    min-width: 32px;
    @apply ml-2 flex items-center justify-center text-sm text-gray-500 p-2;
}

/* Advanced Filters */
.advanced-filters
{
    --left-size: 120px;
    --gap: theme('size.2');

    @apply relative mt-2.5 rounded-md p-4 w-full text-sm text-gray-500 bg-primary-100;
}

.advanced-filters-arrow
{
    position: absolute;
    top: -20px;
    left: 208px;
    display: block;
    width: 20px;
    height: 20px;
    border: 10px solid transparent;
    border-bottom-color: theme('colors.primary.100');
}

.advanced-filters__left
{
    @apply flex flex-col gap-1;

    width: var(--left-size);
}

.advanced-filters__right
{
    @apply flex flex-col gap-1 grow;

    max-width: calc(100% - var(--left-size) - var(--gap));
}

.advanced-filter
{
    @apply cursor-pointer relative flex items-center rounded border border-gray-300 px-2 py-1 bg-white select-none
        text-gray-600
        hover:bg-gray-50
        focus-within:bg-gray-50;
}

.advanced-filter--button
{
    & .mdi-icon,
    & .advanced-filter__caption
    {
        @apply pointer-events-none;
    }
}

.advanced-filter--select
{
    @apply justify-between;

    & select
    {
        @apply cursor-pointer absolute top-0 right-0 bottom-0 left-0 opacity-0;
    }
}

.advanced-filter__icon
{
    @apply inline-block align-middle w-5 h-5 shrink-0;
}

.advanced-filter__caption
{
    @apply inline-flex align-middle truncate;
}

.advanced-filter--has-value
{
    @apply border-primary-500 bg-primary-50 hover:bg-primary-100;

    & .advanced-filter__caption
    {
        @apply text-primary-500;
    }
}
</style>
